import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import { Box, Text } from '@radix-ui/themes';
import { DetectionFlag } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { DialogButton } from 'components/common/dialogs/button';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import {
  AnnotatedVideoPlayer,
  getFireVideoID,
  IAnnotationDetails,
} from 'components/common/video/annotated-player';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IFireEvent } from 'lib_ts/interfaces/i-session-event';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';
import { first } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  identifier: string;
  title: ReactNode;

  hits: ISessionHit[];
  hitID: string;

  onClose: () => void;
  onPlaybackChange: (hitID: string) => void;
  openDetectionIssueDialog: (hit: ISessionHit) => void;
}

export const SessionHitsVideoDialog = (props: IProps) => {
  const current = useAuthStore((s) => s.current);

  const [playbackUrl, setPlaybackUrl] = useState<string>();

  const hit = useMemo(
    () => props.hits.find((h) => h.fire._id === props.hitID),
    [props.hits, props.hitID]
  );

  const hitIndex = useMemo(
    () => props.hits.findIndex((h) => h.fire._id === props.hitID),
    [props.hits, props.hitID]
  );

  const [fireEvent, setFireEvent] = useState<IFireEvent>();

  useEffect(() => {
    if (!hit?.fire._id) {
      return;
    }

    SessionEventsService.getFireByID(hit.fire._id).then((result) =>
      setFireEvent(result)
    );
  }, [hit?.fire._id]);

  const shot = first(fireEvent?.shot);

  const videoData = useMemo(() => {
    const videoID = getFireVideoID({
      annotated_video_id: hit?.fire.annotated_video_id,
      replay_video_id: hit?.fire.replay_video_id,
    });

    if (!videoID) {
      return undefined;
    }

    const output: IAnnotationDetails = {
      videoID: videoID,
      fireID: shot?.fire_id,
      shotID: shot?._id,
      hitterID: hit?.hitter?._id,
    };

    return output;
  }, [hit?.fire, hit?.hitter?._id, shot?._id]);

  const hitterName = hit?.hitter ? hit.hitter.name : 'Anonymous Hitter';

  const subtitle = useMemo(() => {
    if (!hit) {
      return '';
    }

    return [
      hitterName,
      format(parseISO(hit?._created), LOCAL_DATETIME_FORMAT, {
        timeZone: LOCAL_TIMEZONE,
      }),
      `${hitIndex + 1} / ${props.hits.length}`,
    ].join(HINT_DELIMITER);
  }, [hitterName, hit?._created, hitIndex, props.hits.length]);

  const stats = useMemo(() => {
    const output: string[] = [];

    if (!hit?.pitch) {
      return output;
    }

    if (hit.pitch.name) {
      output.push(hit.pitch.name);
    }

    if (hit.pitch.speedMPH !== undefined) {
      output.push(`${hit.pitch.speedMPH.toFixed(1)}mph`);
    }

    if (hit.pitch.xBreakIN !== undefined) {
      output.push(`HB ${(-1 * hit.pitch.xBreakIN).toFixed(1)}in`);
    }

    if (hit.pitch.zBreakIN !== undefined) {
      output.push(`VB ${hit.pitch.zBreakIN.toFixed(1)}in`);
    }

    return output.join(HINT_DELIMITER);
  }, [hit?.pitch]);

  if (!videoData) {
    return null;
  }

  const showFlagButton =
    hit?.tracking_device === TrackingDevice.TrajektVision &&
    current.role === UserRole.admin;

  return (
    <CommonDialog
      title={props.title}
      subtitle={
        <Box mt="3">
          <Text color={RADIX.COLOR.SECONDARY} weight="light" size="3">
            <div>{subtitle}</div>
            <div>{stats}</div>
          </Text>
        </Box>
      }
      identifier={props.identifier}
      content={
        <AnnotatedVideoPlayer
          details={videoData}
          onLoadUrl={(url) => setPlaybackUrl(url)}
          autoplay
        />
      }
      buttons={[
        {
          icon: <ArrowLeftIcon />,
          label: 'common.next',
          // navigates to the record above the current row; blank when the top
          // record is already open
          disabled: hitIndex === 0,
          onClick: () => {
            const aboveHit = props.hits[hitIndex - 1];

            if (!aboveHit) {
              return;
            }

            props.onPlaybackChange(aboveHit.fire._id);
          },
        },
        {
          label: 'common.previous',
          suffixIcon: <ArrowRightIcon />,
          // navigates to the record below the current row; blank when the
          // bottom record is already open
          disabled: hitIndex === props.hits.length - 1,
          onClick: () => {
            const belowHit = props.hits[hitIndex + 1];

            if (!belowHit) {
              return;
            }

            props.onPlaybackChange(belowHit.fire._id);
          },
        },
        {
          icon: <DownloadIcon />,
          variant: RADIX.BUTTON.VARIANT.PRIMARY,
          label: 'common.export-video',
          disabled: !playbackUrl,
          onClick: () => window.open(playbackUrl, '_blank'),
        },
      ]}
      leftFooter={
        showFlagButton ? (
          <DialogButton
            onClick={() => {
              props.openDetectionIssueDialog(hit);
            }}
            color={RADIX.COLOR.ACCENT}
            icon={<DetectionFlag filled={!!hit?.detectionIssue} />}
            label="Detection Issue"
          />
        ) : undefined
      }
      width={RADIX.DIALOG.WIDTH.LG}
      onClose={props.onClose}
    />
  );
};
